import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, model } from '@angular/core';

import { esnPaginationCells } from '../../../shared/types/shell';

/**
 * The list footer that moves through pages: ‹ 1 2 3 4 ›, collapsing long runs into an ellipsis
 * around the current page. Give it `pageCount`, or `total` + `pageSize`; ←/→/Home/End move too.
 */
@Component({
  selector: 'esn-pagination',
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent {
  /**
   * Current page, 1-based.
   */
  readonly page = model(1);

  /**
   * Number of pages; wins over `total` / `pageSize` when both are given.
   */
  readonly pageCount = input<number | null>(null);
  readonly total = input<number | null>(null);
  readonly pageSize = input(12);

  /**
   * Pages shown on each side of the current one before the ellipsis takes over.
   */
  readonly siblings = input(1);

  /**
   * Optional "1–12 of 48" line; receives first item, last item and total. Needs `total`.
   */
  readonly formatRange = input<((from: number, to: number, total: number) => string) | null>(null);

  readonly label = input('Pagination');
  readonly previousLabel = input('Previous page');
  readonly nextLabel = input('Next page');
  readonly pageLabel = input<(page: number) => string>((page) => `Page ${page}`);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly count = computed(() => {
    const explicit = this.pageCount();
    if (explicit != null) return Math.max(0, Math.floor(explicit));
    const total = this.total() ?? 0;
    const size = Math.max(1, this.pageSize());
    return Math.ceil(total / size);
  });
  protected readonly current = computed(() => Math.min(Math.max(1, this.page()), Math.max(1, this.count())));
  protected readonly cells = computed(() => esnPaginationCells(this.current(), this.count(), this.siblings()));
  protected readonly rangeText = computed(() => {
    const format = this.formatRange();
    const total = this.total();
    if (!format || total == null) return null;
    const size = Math.max(1, this.pageSize());
    const from = total === 0 ? 0 : (this.current() - 1) * size + 1;
    return format(from, Math.min(this.current() * size, total), total);
  });

  protected go(target: number): void {
    const next = Math.min(Math.max(1, target), Math.max(1, this.count()));
    if (next !== this.current()) this.page.set(next);
  }

  protected handleKeydown(event: KeyboardEvent): void {
    const moves: Record<string, number> = {
      ArrowLeft: this.current() - 1,
      ArrowRight: this.current() + 1,
      Home: 1,
      End: this.count(),
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    this.go(moves[event.key]);
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('[aria-current="page"]')?.focus());
  }
}
